<template>
  <div
    :class="containerClasses"
    @mouseenter="handleTileExpand(true)"
    @mouseleave="handleTileExpand(false)"
  >
    <slot name="header">
      <Component
        :is="linkTag"
        v-bind="linkAttrs"
        class="d-flex cursor-pointer prevent-select title rs-unstyled rs-nav-title-link"
        :class="linkClasses"
        :to="to"
        @click="handleExpand"
      >
        <div
          v-if="$slots.beforeExpandIcon"
          class="d-flex align-items-center my-1"
          :class="{ 'mr-2': variant === 'collapse' }"
        >
          <slot name="beforeExpandIcon" />
        </div>
        <i
          v-if="variant === 'collapse' && !hideExpandIcon && !thumbnail && !locked"
          :class="`${expandedIcon} expand-icon mr-2`"
        />
        <i
          v-show="icon && !thumbnail"
          :class="iconClasses"
        />

        <RsAvatar
          v-if="!!thumbnail"
          :image="thumbnail"
          :class="thumbnailClasses"
          square
        />

        <slot name="titleLeft" />
        <span
          v-show="!fetching"
          class="flex-grow-0 d-block title-span text-truncate w-100 nav-item-title"
          :class="variant === 'tile_with_popout' ? 'text-center' : ''"
          :title="locked ? lockMessage : theTitle"
        >{{ theTitle }}</span>
        <i
          v-show="fetching"
          class="far fa-spinner fa-spin"
        />
        <div class="flex-fill" />

        <i v-if="locked" class="fal fa-lock rs-text-warning" />
        <slot name="titleRight" />
      </Component>
    </slot>
    <div
      v-if="!noExpand && (hideExpandIcon ? true : isExpanded)"
      :class="{
        'expand-container': true,
        [`variant_${variant}`]: true,
        'shadow-lg': variant !== 'collapse',
        'rs-bg-secondary': !highlight,
        'rs-bg-white': highlight,
        'expand-highlight': highlight,
      }"
    >
      <div
        v-if="variant !== 'collapse'"
        class="expand-title pb-2 pl-1 font-weight-bold"
        :class="{ 'rs-text-white': !highlight, locked }"
      >
        {{ theTitle }}
      </div>
      <slot />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

export type NavigationTreeVariantType = 'tile_with_popout' | 'collapse';

/**
 * The top level of a navigation tree that will contain many navigation items. It can be used in a nested navigation item
 * as well to declare a new nesting section within an already nested navigation portion.
 *
 * Nesting is also not necessary as a `:to` prop may also be passed. If `:to` prop is passed, this will behave like a regular `<RouterLink>`
 */
export default defineComponent({
  props: {
    /**
     * What Icon the tree uses.
     */
    icon: {
      type: String,
      default: null,
    },

    /**
     * Overrides icon with thumbnail when thumbnail is defined.
     */
    thumbnail: {
      type: String,
      default: null,
    },

    /**
     * When nav tree is in active state, what icon will be shown in active state. If none is provided, regular icon prop is used.
     */
    activeIcon: {
      type: String,
      default: null,
    },

    /**
     * Icon size as either `sm` or `lg` variant
     */
    iconSize: {
      type: String as PropType<'sm' | 'lg'>,
      default: 'sm',
    },

    /**
     * Variant on whether we are using a tile with a popout sub nav or a regular collapse-like ux for sub nav.
     */
    variant: {
      type: String as PropType<NavigationTreeVariantType>,
      default: 'collapse',
    },

    /**
     * The title
     */
    title: {
      type: String,
    },

    /**
     * This allows us to fetch a title asyncrhonously (ex: a property name from an API call)
     */
    titleFetcher: {
      type: Function as PropType<() => Promise<string>>,
      default: null,
    },

    /**
     * When a navigation tree is not expandable, or is conditionally expandable, we can toggle expand icon visibility.
     */
    hideExpandIcon: {
      type: Boolean,
      default: false,
    },

    /**
     * Synonymous with the `:to` prop that is used by Vue Router's `<RouterLink>` component.
     */
    to: {
      type: [ String, Object ],
      default: null,
    },

    /**
     * Initial expanded state can be controlled by the `expanded` prop.
     */
    expanded: {
      type: Boolean,
      default: false,
    },

    /**
     * A state that changes the background color of the navigation tree to a light purple
     */
    highlight: {
      type: Boolean,
      default: false,
    },

    /**
     * Determines active state of the component, which will toggle some classes and present alternate UI.
     * Used often in conjunction with `expanded` prop for current state.
     */
    active: {
      type: Boolean,
      default: false,
    },

    /**
     * When the top level nav item, tree top has some specific styling
     */
    treeTop: {
      type: Boolean,
      default: false,
    },

    /**
     * No expand - basically navigation tree is just a link.
     * Applies a necessary styling for the tile variant to be scoped to parent width.
     */
    noExpand: {
      type: Boolean,
      default: false,
    },

    /**
     * Adds a specific state to navigation that adds a lock icon
     */
    locked: {
      type: Boolean,
      default: false,
    },

    /**
     * Adds a message to the locked state
     */
    lockMessage: {
      type: String,
      default: 'Contact your Rentsync account manager to enable this feature.',
    },
  },
  emits: ['click'],

  data() {
    return {
      isExpanded: false as boolean,
      fetchedTitle: '' as string,
      fetching: false as boolean,
    };
  },

  computed: {
    /**
     * Some logic that returns the classes for the main component's `<div>`
     */
    containerClasses(): Record<string, boolean> {
      return {
        container_highlight: this.highlight,
        'tree-top': this.treeTop,
        active: this.active,
        'rs-partial-navigation-tree flex-grow-1': true,
        'pt-3 d-block position-relative': this.variant === 'tile_with_popout',
        'text-truncate': this.variant === 'collapse',
        [`container_variant_${this.variant}`]: true,
        'rs-bg-secondary': !this.highlight,
        'rs-bg-white': this.highlight,
        'w-100': this.variant === 'tile_with_popout' && this.noExpand,
      };
    },

    /**
     * Determines which HTML/Router tag to render based on `to` and `locked`.
     */
    linkTag(): string {
      if (this.to && !this.locked) {
        if (typeof this.to === 'string' && this.to.startsWith('http')) {
          return 'a'; // external link
        }
        return 'RouterLink'; // vue-router link
      }
      return 'div';
    },

    /**
     * Returns the proper attributes for <a>, <RouterLink>, or <div>.
     */
    linkAttrs(): Record<string, string | object> {
      if (this.linkTag === 'a') {
        // target: _blank opens a new tab and rel values are for better security and privacy
        return { href: this.to, target: '_blank', rel: 'noopener noreferrer' };
      }
      if (this.linkTag === 'RouterLink') {
        return { to: this.to };
      }
      return {};
    },

    /**
     * Some logic that returns the classes for the component `<RouterLink>` based on prop variants passed.
     */
    linkClasses(): Record<string, boolean> {
      return {
        [`icon-${this.iconSize}`]: true,
        'text-truncate': true,
        'align-items-center position-relative nav-item-height': this.variant === 'collapse',
        'tile flex-column align-items-center pb-2': this.variant === 'tile_with_popout',
        'no-expand': this.hideExpandIcon,
        'tree-locked': this.locked,
      };
    },

    /**
     * Icon classes determined by active state and component variant (tile, collapse)
     */
    iconClasses(): Record<string, boolean> {
      return {
        [this.icon]: !this.activeIcon || !this.active,
        [this.activeIcon]: !!this.activeIcon && this.active,
        'nav-icon': true,
        'mr-2': this.variant === 'collapse',
      };
    },

    /**
     * When a thumbnail prop is defined, the classes assigned to thumbnail
     */
    thumbnailClasses(): Record<string, boolean> {
      return {
        'tree-thumbnail': true,
        'mr-2': this.variant === 'collapse',
        'mb-1': this.variant !== 'collapse',
      };
    },

    /**
     * Expanded icon state based on whether component is expanded or not.
     */
    expandedIcon(): string {
      return this.isExpanded ? 'far fa-minus' : 'far fa-plus';
    },

    /**
     * Returns title state based on the `title` or `titleFetcher` prop.
     */
    theTitle(): string {
      return this.fetching ? '' : (this.fetchedTitle || this.title || '').trim();
    },
  },

  watch: {
    expanded: {
      immediate: true,
      handler() {
        if (this.variant === 'collapse') {
          this.isExpanded = this.expanded;
        }
      },
    },

    variant: {
      immediate: true,
      handler() {
        if (this.variant === 'tile_with_popout') {
          this.isExpanded = false;
        }
      },
    },
  },

  mounted() {
    this.getTitle();
  },

  methods: {
    /**
     * Expand icon click handler
     */
    handleExpand() {
      if (!this.locked) {
        if (this.variant === 'collapse') {
          this.isExpanded = !this.isExpanded;
        }
        this.$emit('click');
      }
    },

    handleTileExpand(value: boolean) {
      if (this.variant !== 'collapse') {
        this.isExpanded = value;
      }
    },

    /**
     * Fetches title with the `titleFetcher` prop is filled out. Sets a loading state while fetcher promise is resolving.
     */
    async getTitle() {
      if (this.titleFetcher && !(this.fetchedTitle ?? '').trim().length) {
        this.fetching = true;
        this.fetchedTitle = await this.titleFetcher();
        this.fetching = false;
      }
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/navigation.scss';
</style>
